import { useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/shared/lib/cn'

interface MagneticProps {
  children: React.ReactNode
  className?: string
  /** How far the element follows the cursor (px). Default 8. */
  strength?: number
  /** Inner content gets a smaller pull for a layered effect. */
  innerStrength?: number
}

/**
 * Magnetic wrapper — the child gently follows the cursor when hovered.
 * Uses transform only, GPU-cheap. Honors `prefers-reduced-motion`
 * (renders the child statically).
 *
 * Wrap a Button / Link / Card with it. The wrapper is `display:inline-block`
 * so it doesn't change layout.
 */
export function Magnetic({
  children,
  className,
  strength = 8,
  innerStrength = 4,
}: MagneticProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const reducedMotion = useReducedMotion()

  if (reducedMotion) {
    return <span className={cn('inline-block', className)}>{children}</span>
  }

  const onMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    const dx = (e.clientX - cx) / (rect.width / 2)
    const dy = (e.clientY - cy) / (rect.height / 2)
    setPos({ x: dx * strength, y: dy * strength })
  }

  const onLeave = () => setPos({ x: 0, y: 0 })

  return (
    <motion.span
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      animate={{ x: pos.x, y: pos.y }}
      transition={{ type: 'spring', stiffness: 220, damping: 18, mass: 0.4 }}
      className={cn('inline-block', className)}
    >
      <motion.span
        animate={{ x: pos.x * (innerStrength / strength), y: pos.y * (innerStrength / strength) }}
        transition={{ type: 'spring', stiffness: 220, damping: 18, mass: 0.4 }}
        className="inline-block"
      >
        {children}
      </motion.span>
    </motion.span>
  )
}
